/**
 * leadFilterConfig.js — single source of truth for Leads page + Campaign filter panel.
 *
 * Adding a new filter here automatically lights it up in:
 *   - LeadFilterPanel (right-side drawer)
 *   - CustomFilterBuilder (Leads page panel)
 *   - Campaign create-step-2 filter panel (mirrored)
 */

export const FILTER_FIELDS = [
  // §1 Address-derived (multi-select / tag-input)
  { key: "state",        label: "State",                  type: "multi-state",  group: "Location",   testKey: "state" },
  { key: "city",         label: "City",                   type: "tag",          group: "Location",   maxTags: 10, testKey: "city" },
  { key: "zip",          label: "ZIP code",               type: "tag",          group: "Location",   maxTags: 10, testKey: "zip" },
  { key: "regions",      label: "Region",                 type: "multi-region", group: "Location",   testKey: "region" },

  // Existing canonical filters
  { key: "sources",       label: "Lead Source",           type: "multi-source",     group: "Lead",       testKey: "source" },
  { key: "specialties",   label: "Specialty",             type: "multi-specialty",  group: "Practice",   testKey: "spec" },
  { key: "products",      label: "Products of Interest",  type: "multi-product",    group: "Interest",   testKey: "prod" },
  { key: "budgets",       label: "Budget Range",          type: "multi-budget",     group: "Interest",   testKey: "budget" },
  { key: "timelines",     label: "Implementation Timeline", type: "multi-timeline", group: "Interest",   testKey: "timeline" },
  { key: "ehrs",          label: "Current EHR",           type: "multi-ehr",        group: "Practice",   testKey: "ehr" },
  { key: "ratings",       label: "Lead Rating",           type: "multi-rating",     group: "Lead",       testKey: "rating" },
  { key: "practiceTypes", label: "Practice Type",         type: "multi-practice",   group: "Practice",   testKey: "ptype" },
];

export const STATES_FULL = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
];

/**
 * Lookup field meta by key (also matches lower-case label or testKey).
 */
export function findField(query) {
  if (!query) return null;
  const q = String(query).toLowerCase();
  return FILTER_FIELDS.find(
    (f) => f.key.toLowerCase() === q || f.label.toLowerCase().includes(q) || f.testKey === q,
  );
}

/**
 * Initial state shape for the new filters added in §2/§3.
 * Existing keys (sources / specialties / etc.) are owned by EMPTY_LEAD_FILTERS
 * in LeadFilterPanel — kept there to minimise diff churn.
 */
export const EMPTY_EXTRA_FILTERS = {
  state: [],   // ["CA","TX",…]
  city: [],    // ["Chicago",…]  (case-insensitive)
  zip: [],     // ["60601",…]
  lastContact: "any", // any | today | yesterday | 2_7 | 7_14 | 14_30 | 30_plus | never
  // Iter 195.14 — "Imported" date-range filter on the /leads toolbar.
  // `null` = no filter (default, replicates pre-Iter behaviour so
  // existing users see zero change until they open the dropdown).
  // When set: `{ from: "YYYY-MM-DD"|null, to: "YYYY-MM-DD"|null }`.
  loadedDate: null,
};

/** Bucket presets for the Last Contact filter (Section 2 — agentic-AI spec). */
export const LAST_CONTACT_BUCKETS = [
  { id: "any",       label: "Any time" },
  { id: "today",     label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "2_7",       label: "2–7 days ago" },
  { id: "7_14",      label: "7–14 days ago" },
  { id: "14_30",     label: "14–30 days ago" },
  { id: "30_plus",   label: "30+ days (stale)" },
  { id: "never",     label: "Never contacted" },
];

/** Returns true when the lead's last-contact date falls in the chosen bucket.
 *
 *  Iter 177.9 (Lisa #a97a62fd Feb 2026) — extended to accept either a
 *  preset bucket id ("today", "30_plus", …) OR a structured custom
 *  range `{ from: "YYYY-MM-DD", to: "YYYY-MM-DD" }`. The toolbar's
 *  "Custom range…" picker passes the structured form. Either bound
 *  is optional; missing means "open-ended" on that side.
 */
export function applyLastContactFilter(lead, bucketId) {
  if (!bucketId || bucketId === "any") return true;
  const iso = lead.last_contact_date || lead.last_contact_at || lead.last_activity_at
    || (Array.isArray(lead.activities) && lead.activities[0]?.at) || null;

  // Structured custom-range form: { from, to } (both optional).
  if (bucketId && typeof bucketId === "object" && (bucketId.from || bucketId.to)) {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return false;
    if (bucketId.from) {
      const fromT = new Date(bucketId.from + "T00:00:00").getTime();
      if (!Number.isNaN(fromT) && t < fromT) return false;
    }
    if (bucketId.to) {
      // Inclusive — include all of the `to` day up to 23:59:59.999.
      const toT = new Date(bucketId.to + "T23:59:59.999").getTime();
      if (!Number.isNaN(toT) && t > toT) return false;
    }
    return true;
  }

  if (bucketId === "never") return !iso;
  if (!iso) return false;
  // Iter 216 (Aug 2026 · Bug — "Today" and "Yesterday" filters were
  // showing +24 hrs of extra results). Old code:
  //   `const days = Math.floor((Date.now() - iso.getTime()) / 86400000)`
  // measured elapsed HOURS ÷ 24, not calendar-day difference. Result:
  // a lead contacted Mon 8:00 PM at Tue 3:00 PM (19h ago) got
  // `days=0` and matched "today"; a Sun 8:00 PM call (43h ago) got
  // `days=1` and matched "yesterday". Anchor both timestamps to
  // LOCAL midnight so we get the true calendar-day delta the label
  // implies. Rounded (not floor()'d) so DST transitions can't shift
  // the delta by ±1 hr → ±1 day.
  const _contactedAt = new Date(iso);
  if (Number.isNaN(_contactedAt.getTime())) return false;
  const _now = new Date();
  const _todayMidnight = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  const _contactedMidnight = new Date(_contactedAt.getFullYear(), _contactedAt.getMonth(), _contactedAt.getDate());
  const days = Math.round((_todayMidnight.getTime() - _contactedMidnight.getTime()) / 86400000);
  if (Number.isNaN(days)) return false;
  // Iter 216 — fixed bucket overlaps too. Old ranges:
  //   2_7  → [2,7],  7_14 → [7,14],  14_30 → [14,30],  30_plus → [30, ∞)
  // Day 7 satisfied BOTH 2_7 AND 7_14 (same for 14 + 30). Now non-
  // overlapping so counts / previews can't double-attribute.
  switch (bucketId) {
    case "today":     return days === 0;
    case "yesterday": return days === 1;
    case "2_7":       return days >= 2  && days <= 7;
    case "7_14":      return days >= 8  && days <= 14;
    case "14_30":     return days >= 15 && days <= 30;
    case "30_plus":   return days >= 31;
    default:          return true;
  }
}

/**
 * Iter 195.14 — "Imported" date-range filter (Leads toolbar).
 *
 * Filters by `lead.created_at` (the moment the record landed in our
 * `leads` collection — i.e., when the CSV import ran, or when a user
 * created it manually). Accepts:
 *   - `null` / `undefined` → no filter (default, whole /leads shows).
 *   - `{ from: "YYYY-MM-DD"|null, to: "YYYY-MM-DD"|null }` — either
 *     bound is optional; open-ended on the missing side.
 * Inclusive on both ends: `to` extends to 23:59:59.999 of that day.
 *
 * Deliberately does NOT reuse `applyLastContactFilter`: users have
 * asked for two independent date filters, and conflating them makes
 * the URL/state model confusing.
 */
export function applyLoadedDateFilter(lead, range) {
  if (!range) return true;
  if (typeof range !== "object") return true;
  const { from, to } = range;
  if (!from && !to) return true;
  const iso = lead.created_at || lead.imported_at || null;
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  if (from) {
    const fromT = new Date(from + "T00:00:00").getTime();
    if (!Number.isNaN(fromT) && t < fromT) return false;
  }
  if (to) {
    const toT = new Date(to + "T23:59:59.999").getTime();
    if (!Number.isNaN(toT) && t > toT) return false;
  }
  return true;
}



/**
 * Apply the §1/§2/§3 location filters to a leads array. Reads from the
 * primary address (addresses[0]) AND legacy scalar fields, so it works
 * before and after the lead-data normalization pass.
 */
export function applyLocationFilters(lead, extra) {
  const addr = (Array.isArray(lead.addresses) && lead.addresses[0]) || {};
  const leadState = String(addr.state || lead.state || "").toUpperCase().trim();
  const leadCity = String(addr.city || lead.city || "").toLowerCase().trim();
  const leadZip = String(addr.zip || lead.zip || "").trim();

  if ((extra.state || []).length > 0 && !extra.state.includes(leadState)) return false;
  if ((extra.city || []).length > 0) {
    const targets = extra.city.map((s) => String(s || "").toLowerCase().trim());
    if (!targets.some((t) => leadCity && (leadCity.includes(t) || t.includes(leadCity)))) return false;
  }
  if ((extra.zip || []).length > 0) {
    if (!extra.zip.some((z) => leadZip && (leadZip === z || leadZip.startsWith(String(z).slice(0, 5))))) return false;
  }
  return true;
}

/**
/**
 * Free-text search across the demanded set of fields.
 *
 * Iter 184.2 (Feb 2026 — Himagna #bb53ea22 + Himagna #f0b50009):
 * normalise both ends so the client-side filter matches the server-
 * side fuzzy logic.
 *   • Phone queries: strip non-digits on both sides so
 *     "(718) 336-3900" matches a stored "718-336-3900".
 *   • Name queries with trailing punctuation: drop trailing
 *     comma/period chunks ("Naum Meyerovich, M.D" → "naum meyerovich")
 *     so partial-credential suffixes don't break the lookup.
 *   • All comparisons are diacritic-insensitive (NFD strip).
 */
export function applyTextSearch(lead, q) {
  const raw = String(q || "").trim();
  if (!raw) return true;
  // Normaliser shared by the needle + every haystack entry: lowercase,
  // strip diacritics, collapse whitespace.
  const norm = (s) => String(s ?? "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  // Phone-shaped needles: digit-only on both sides.
  const isPhoneShaped = /^[\d\-+()\s.]+$/.test(raw) && (raw.match(/\d/g) || []).length >= 3;
  const digitsOnly = (s) => String(s ?? "").replace(/\D/g, "");
  const needle = norm(raw);
  const needlePhoneDigits = isPhoneShaped ? digitsOnly(raw) : null;
  // For "Name, M.D" / "Name MD" — drop the suffix after a comma so the
  // root name still hits a haystack that's stored without the suffix.
  const needleRoot = needle.replace(/,.*$/, "").trim();

  const haystackRaw = [
    lead.title, lead.company, lead.contact_name,
    // Iter 170.8 (Himagna #16 — "search bar can't find lead I just
    // contacted today" 2026-06-05). Practice records often have a
    // `clinic` / `practice_name` / `business_name` that's distinct
    // from the contact name; reps search by practice (e.g. "Homedical
    // Associates") and used to get no results because the haystack
    // skipped those fields.
    lead.clinic, lead.practice_name, lead.business_name,
    lead.specialty, lead.nppes?.specialty_group,
    lead.address, lead.city, lead.zip,
    lead.email, lead.phone,
    ...(Array.isArray(lead.contacts) ? lead.contacts.flatMap((c) => [c.name, c.email, c.phone, ...((c.emails || []).map((e) => e.address)), ...((c.phones || []).map((p) => p.number))]) : []),
    ...(Array.isArray(lead.decision_makers) ? lead.decision_makers.flatMap((c) => [c.name, c.email, c.phone]) : []),
    ...(Array.isArray(lead.locations) ? lead.locations.flatMap((l) => [l.phone, l.address, l.city]) : []),
  ].filter(Boolean);

  // Phone path — match by digits-only inclusion. Cheaper than the text
  // path and works regardless of stored format.
  if (needlePhoneDigits && needlePhoneDigits.length >= 3) {
    const target = needlePhoneDigits;
    for (const h of haystackRaw) {
      const hd = digitsOnly(h);
      if (hd && hd.includes(target)) return true;
    }
    // Fall through to the text path too — sometimes a rep types a
    // mostly-numeric value that should also match a name (e.g. a
    // zip "10001").
  }

  const haystack = haystackRaw.map(norm);
  return haystack.some((h) => h.includes(needle) || (needleRoot && h.includes(needleRoot)));
}

/**
 * Encode a filter set into URL search params (for §2C persistence).
 * Empties are stripped so the URL stays compact.
 */
export function filtersToUrl(state) {
  const out = {};
  Object.entries(state || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      out[key] = value.join(",");
    } else if (typeof value === "string" && value.trim() !== "") {
      out[key] = value;
    } else if (typeof value === "boolean" && value) {
      out[key] = "1";
    } else if (
      // Iter 195.14 — date-range object `{from, to}` (loadedDate filter).
      typeof value === "object" &&
      (Object.prototype.hasOwnProperty.call(value, "from") ||
       Object.prototype.hasOwnProperty.call(value, "to"))
    ) {
      const f = value.from || "";
      const t = value.to || "";
      if (f || t) out[key] = `${f}..${t}`;
    }
  });
  return out;
}

/**
 * Decode URL → state. `keys` is a list of expected keys; arrays are
 * split on `,`. Booleans recognised when value === "1" or "true".
 */
export function urlToFilters(searchParams, schema) {
  const out = {};
  Object.entries(schema || {}).forEach(([key, defaultValue]) => {
    const raw = searchParams.get(key);
    if (raw == null) {
      out[key] = Array.isArray(defaultValue) ? [...defaultValue] : defaultValue;
      return;
    }
    if (Array.isArray(defaultValue)) {
      out[key] = raw.split(",").map((s) => s.trim()).filter(Boolean);
    } else if (typeof defaultValue === "boolean") {
      out[key] = raw === "1" || raw === "true";
    } else if (
      // Iter 195.14 — date-range object filter. Default is `null` and the
      // raw form is "YYYY-MM-DD..YYYY-MM-DD" (either side optional).
      defaultValue === null && raw.includes("..")
    ) {
      const [f, t] = raw.split("..", 2);
      out[key] = { from: f || null, to: t || null };
    } else {
      out[key] = raw;
    }
  });
  return out;
}

/**
 * Plain-language summary for the active filter indicator bar.
 */
export function summarizeFilters({ search, filters, extra, stage, subStatus }) {
  const parts = [];
  if (search) parts.push(`"${search}"`);
  if (stage && stage !== "all") parts.push(`stage: ${stage}`);
  if (subStatus && subStatus !== "all") parts.push(`sub-status: ${subStatus}`);
  if ((filters?.sources || []).length) parts.push(`source · ${filters.sources.length}`);
  if ((filters?.specialties || []).length) parts.push(`specialty · ${filters.specialties.length}`);
  if ((filters?.products || []).length) parts.push(`products · ${filters.products.length}`);
  if ((filters?.budgets || []).length) parts.push(`budget · ${filters.budgets.length}`);
  if ((filters?.timelines || []).length) parts.push(`timeline · ${filters.timelines.length}`);
  if ((filters?.ehrs || []).length) parts.push(`EHR · ${filters.ehrs.length}`);
  if ((filters?.ratings || []).length) parts.push(`rating · ${filters.ratings.length}`);
  if ((filters?.practiceTypes || []).length) parts.push(`practice type · ${filters.practiceTypes.length}`);
  if ((filters?.regions || []).length) parts.push(`region · ${filters.regions.length}`);
  if ((extra?.state || []).length) parts.push(`state · ${extra.state.length}`);
  if ((extra?.city || []).length) parts.push(`city · ${extra.city.length}`);
  if ((extra?.zip || []).length) parts.push(`zip · ${extra.zip.length}`);
  return parts.join(" · ");
}

export function totalActive({ search, filters, extra, stage, subStatus }) {
  let n = 0;
  if (search) n += 1;
  if (stage && stage !== "all") n += 1;
  if (subStatus && subStatus !== "all") n += 1;
  Object.values(filters || {}).forEach((v) => { if (Array.isArray(v) && v.length > 0) n += v.length; });
  Object.values(extra || {}).forEach((v) => { if (Array.isArray(v) && v.length > 0) n += v.length; });
  return n;
}
